import { useMemo } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ActivityData {
  activity_date: string;
  total_activity: number;
}

interface ActivityHeatMapProps {
  data: ActivityData[];
  title: string;
  description?: string;
}

export const ActivityHeatMap = ({ data, title, description }: ActivityHeatMapProps) => {
  const { weeks, maxActivity } = useMemo(() => {
    // Group data by weeks (Sunday to Saturday)
    const weekMap = new Map<string, ActivityData[]>();
    
    data.forEach((item) => {
      const date = new Date(item.activity_date);
      const sunday = new Date(date);
      sunday.setDate(date.getDate() - date.getDay());
      const weekKey = sunday.toISOString().split('T')[0];
      
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, []);
      }
      weekMap.get(weekKey)!.push(item);
    });

    // Convert to array of weeks
    const weeks = Array.from(weekMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, days]) => {
        // Ensure we have all 7 days
        const weekDays = Array(7).fill(null).map((_, i) => {
          const found = days.find(d => new Date(d.activity_date).getDay() === i);
          return found || { activity_date: '', total_activity: 0 };
        });
        return weekDays;
      });

    const maxActivity = Math.max(...data.map(d => d.total_activity), 1);

    return { weeks, maxActivity };
  }, [data]);

  const getActivityLevel = (activity: number): string => {
    if (activity === 0) return 'bg-muted';
    const ratio = activity / maxActivity;
    if (ratio >= 0.75) return 'bg-primary';
    if (ratio >= 0.5) return 'bg-primary/70';
    if (ratio >= 0.25) return 'bg-primary/40';
    return 'bg-primary/20';
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <div className="space-y-2">
            <div className="flex gap-1">
              <div className="w-12 flex flex-col gap-1 text-xs text-muted-foreground">
                {dayLabels.map((day, i) => (
                  <div key={i} className="h-3 flex items-center">
                    {i % 2 === 1 ? day : ''}
                  </div>
                ))}
              </div>
              <div className="flex-1 overflow-x-auto">
                <div className="flex gap-1">
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-1">
                      {week.map((day, dayIndex) => (
                        <Tooltip key={dayIndex}>
                          <TooltipTrigger asChild>
                            <div
                              className={`h-3 w-3 rounded-sm transition-colors ${
                                day.activity_date ? getActivityLevel(day.total_activity) : 'bg-transparent'
                              }`}
                            />
                          </TooltipTrigger>
                          {day.activity_date && (
                            <TooltipContent>
                              <div className="text-xs">
                                <div className="font-medium">{formatDate(day.activity_date)}</div>
                                <div className="text-muted-foreground">
                                  {day.total_activity} {day.total_activity === 1 ? 'activity' : 'activities'}
                                </div>
                              </div>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
              <span>Less</span>
              <div className="flex gap-1">
                <div className="h-3 w-3 rounded-sm bg-muted" />
                <div className="h-3 w-3 rounded-sm bg-primary/20" />
                <div className="h-3 w-3 rounded-sm bg-primary/40" />
                <div className="h-3 w-3 rounded-sm bg-primary/70" />
                <div className="h-3 w-3 rounded-sm bg-primary" />
              </div>
              <span>More</span>
            </div>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};
